export function setExpanded(button, panel, open) {
  button.setAttribute('aria-expanded', open ? 'true' : 'false');
  panel.hidden = !open;
  return open;
}

export const isOutside = (target, roots) => !roots.some(root => root && root.contains(target));

// The mega panels dim the page under the header (07 Interactive Prototype: a .35 scrim with a 4px blur, motion audit fix 2).
// The scrim shows while any panel is open; a click on it lands outside every ns-mega, which closes the panel.
export function syncMegaScrim(doc) {
  const scrim = doc.querySelector('[data-ns-mega-scrim]');
  if (scrim) scrim.hidden = !doc.querySelector('[data-ns-mega-button][aria-expanded="true"]');
}

// The Blogs panel opens on hover (07 Interactive Prototype: mouseenter, with a bridge to the panel; motion audit fix 17):
// with a mouse it opens as the pointer arrives and closes 150ms after it leaves, long enough to cross to the panel. Touch
// and pens open it with their tap. A click on the hovered button keeps the open panel, so the click never undoes the hover.
export const HOVER_CLOSE_MS = 150;
export function hoverMenu({ open, close, delay = HOVER_CLOSE_MS, later = setTimeout, cancel = clearTimeout }) {
  let timer = null;
  const stop = () => {
    if (timer === null) return;
    cancel(timer);
    timer = null;
  };
  return {
    enter(event) {
      if (event.pointerType !== 'mouse') return;
      stop();
      open();
    },
    leave(event) {
      if (event.pointerType !== 'mouse') return;
      stop();
      timer = later(() => { timer = null; close(); }, delay);
    },
  };
}
export const nextOpen = (expanded, hovered) => (expanded && hovered ? true : !expanded);

// Every theme dialog closes from its scrim, as the prototype's overlays do (fix 2). The press must start and end on the
// scrim, so a text selection dragged out of a form never closes it, and a keyboard press (no pointerdown) never does.
export const isBackdropClick = (rect, x, y) => x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;

export function dismissOnBackdrop(doc) {
  let pressed = null;
  const scrimOf = event => {
    const dialog = event.target;
    if (dialog?.tagName !== 'DIALOG' || !dialog.open || !dialog.classList?.contains('ns-dialog')) return null;
    return isBackdropClick(dialog.getBoundingClientRect(), event.clientX, event.clientY) ? dialog : null;
  };
  doc.addEventListener('pointerdown', event => { pressed = scrimOf(event); });
  doc.addEventListener('click', event => {
    const dialog = scrimOf(event);
    if (dialog && dialog === pressed) dialog.close();
    pressed = null;
  });
}

// Pinned header (07 Interactive Prototype #site-header): from 750px the header section sticks to the top (ns-header.css) and
// turns frosted once the page has scrolled more than 12px, as in the prototype. Its height goes to --ns-sticky-offset, which
// the sticky side columns, the root scroll padding (anchor jumps and keyboard focus) and the mega panel read; on phones the
// header scrolls away, so the offset is 0.
export const SCROLLED_Y = 12;
export const isScrolled = y => Number(y) > SCROLLED_Y;
export const stickyOffset = (position, height) => (position === 'sticky' ? Math.max(0, Math.round(Number(height) || 0)) : 0);

// Phones (07 Interactive Prototype Mobile onScroll): the header stays in the flow until its logo bar has scrolled away, then
// pins (data-pinned). The direction (data-dir, after 4px of travel, as in the prototype) picks what shows at the top:
// scrolling down only the campaign strip, scrolling up the bar above it. ns-header.css reads both below 750px only.
export const DIR_SLACK = 4;
export const scrollDir = (y, last, previous = 'up') => (y > last + DIR_SLACK ? 'down' : y < last - DIR_SLACK ? 'up' : previous);
export const isPinned = (y, pinY) => pinY > 0 && Number(y) > pinY;
const PHONE_QUERY = '(max-width: 749px)';

// Owner request 2026-10-23: from 750px the sticky header slides away while the shopper scrolls down and comes back on the
// way up. It stays near the top of the page, and while a mega menu, a dialog (search, drawer, cart) or keyboard focus is in it.
export const HIDE_AFTER = 80;
export const shouldHideHeader = ({ y, dir, height, busy }) => !busy && dir === 'down' && Number(y) > (Number(height) || 0) + HIDE_AFTER;
const isBusy = (doc, header) => Boolean(doc.querySelector('[data-ns-mega-button][aria-expanded="true"], dialog[open]')) || Boolean(header.matches?.(':focus-within'));

// The announcement bar closes for this visitor and this message: a new message shows again. Storage may be blocked.
export const ANNOUNCE_KEY = 'ns-announce-closed';
export function announceDismissed(storage, message) {
  try { return Boolean(storage) && storage.getItem(ANNOUNCE_KEY) === String(message); } catch { return false; }
}
export function dismissAnnounce(storage, message) {
  try { storage?.setItem(ANNOUNCE_KEY, String(message)); } catch { /* private mode: closed for this page view only */ }
}

// The rotating announcement shows one message at a time: the next one that is not hidden (a paused welcome line stays hidden).
export const ROTATE_MS = 5500;
export function nextMessage(hidden, current) {
  const count = hidden.length;
  for (let step = 1; step <= count; step += 1) {
    const index = (current + step) % count;
    if (!hidden[index]) return index;
  }
  return current;
}
export function rotateAnnounce(bar, view, doc = view.document) {
  if (!bar || bar.dataset.nsRotating) return;
  bar.dataset.nsRotating = '1';
  if (view.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const messages = [...bar.querySelectorAll('.ns-announce__msg')];
  let paused = false;
  const tick = () => {
    if (paused || doc.hidden || bar.hidden) return;
    const hidden = messages.map(message => message.hidden);
    const current = messages.findIndex(message => message.classList.contains('is-active'));
    const next = nextMessage(hidden, current);
    if (next === current) return;
    messages[current]?.classList.remove('is-active');
    messages[next].classList.add('is-active');
  };
  const hold = value => () => { paused = value; };
  bar.addEventListener('mouseenter', hold(true));
  bar.addEventListener('mouseleave', hold(false));
  bar.addEventListener('focusin', hold(true));
  bar.addEventListener('focusout', hold(false));
  view.setInterval(tick, ROTATE_MS);
}

export function pinHeader(doc, view) {
  const header = doc.querySelector('.ns-header');
  if (!header || header.dataset.nsPinned) return;
  header.dataset.nsPinned = '1';
  const wrap = header.closest('.ns-header-section') || header;
  let hidden = false;
  const measure = () => doc.documentElement.style.setProperty('--ns-sticky-offset', `${hidden ? 0 : stickyOffset(view.getComputedStyle(wrap).position, header.offsetHeight)}px`);
  const bar = header.querySelector?.('.ns-header__bar');
  const phone = view.matchMedia?.(PHONE_QUERY);
  let pinY = 0;
  let lastY = Number(view.scrollY) || 0;
  let dir = 'up';
  // Where the bar ends in the page, read while it is in the flow (pinned, it is fixed and its slot is padding).
  const measurePin = () => {
    if (header.hasAttribute?.('data-pinned')) return;
    pinY = phone?.matches && bar ? Math.round(bar.getBoundingClientRect().bottom + (Number(view.scrollY) || 0)) : 0;
  };
  const update = () => {
    const y = Number(view.scrollY) || 0;
    header.toggleAttribute('data-scrolled', isScrolled(y));
    dir = scrollDir(y, lastY, dir);
    lastY = y;
    header.dataset.dir = dir;
    header.toggleAttribute('data-pinned', isPinned(y, pinY));
    const sticky = view.getComputedStyle(wrap).position === 'sticky';
    const hide = sticky && shouldHideHeader({ y, dir, height: header.offsetHeight, busy: isBusy(doc, header) });
    if (hide !== hidden) {
      hidden = hide;
      wrap.toggleAttribute?.('data-hidden', hidden);
      measure();
    }
  };
  let queued = false;
  const onScroll = () => {
    if (queued) return;
    queued = true;
    view.requestAnimationFrame(() => {
      queued = false;
      update();
    });
  };
  const onResize = () => {
    header.toggleAttribute('data-pinned', false);
    measure();
    measurePin();
    update();
  };
  view.addEventListener('scroll', onScroll, { passive: true });
  view.addEventListener('resize', onResize);
  // The strip's close button, a font swap or a wrapped announcement change the height without a resize.
  if (typeof view.ResizeObserver === 'function') new view.ResizeObserver(() => { measure(); measurePin(); }).observe(header);
  measure();
  measurePin();
  update();
}

if (typeof document !== 'undefined') {
  const pin = () => pinHeader(document, window);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pin);
  else pin();
  document.addEventListener('shopify:section:load', pin);
}

if (typeof document !== 'undefined') {
  const startRotation = () => document.querySelectorAll('[data-ns-rotate]').forEach(bar => rotateAnnounce(bar, window));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startRotation);
  else startRotation();
  document.addEventListener('shopify:section:load', startRotation);
  const storage = () => { try { return window.localStorage; } catch { return null; } };
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-ns-announce-close]');
    const bar = button?.closest('[data-ns-announce]');
    if (!bar) return;
    dismissAnnounce(storage(), bar.dataset.nsAnnounce);
    bar.hidden = true;
  });
}

if (typeof customElements !== 'undefined' && !customElements.get('ns-mega')) {
  customElements.define('ns-mega', class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-ns-mega-button]');
      this.panel = this.querySelector('[data-ns-mega-panel]');
      if (!this.button || !this.panel) return;
      this.toggle = open => {
        if (open) for (const other of document.querySelectorAll('ns-mega')) if (other !== this) other.toggle?.(false);
        setExpanded(this.button, this.panel, open);
        syncMegaScrim(document);
      };
      if (this.hasAttribute('data-ns-mega-hover') && window.matchMedia('(hover: hover)').matches) {
        const menu = hoverMenu({ open: () => this.toggle(true), close: () => this.toggle(false) });
        this.onEnter = event => menu.enter(event);
        this.onLeave = event => menu.leave(event);
        this.addEventListener('pointerenter', this.onEnter);
        this.addEventListener('pointerleave', this.onLeave);
      }
      this.onClick = () => this.toggle(nextOpen(this.button.getAttribute('aria-expanded') === 'true', Boolean(this.onEnter) && this.matches(':hover')));
      this.onKey = event => {
        if (event.key === 'Escape' && this.button.getAttribute('aria-expanded') === 'true') {
          this.toggle(false);
          this.button.focus();
        }
      };
      this.onDocument = event => {
        if (this.button.getAttribute('aria-expanded') === 'true' && isOutside(event.target, [this])) this.toggle(false);
      };
      this.button.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      document.addEventListener('click', this.onDocument);
    }
    disconnectedCallback() {
      this.removeEventListener('pointerenter', this.onEnter);
      this.removeEventListener('pointerleave', this.onLeave);
      this.button?.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onDocument);
    }
  });
}

if (typeof document !== 'undefined') {
  dismissOnBackdrop(document);
  document.addEventListener('click', event => {
    const opener = event.target.closest?.('[data-ns-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-ns-dialog-open'));
      if (dialog && !dialog.open) {
        document.querySelectorAll('dialog[open].ns-dialog').forEach(open => open.close());
        dialog.showModal();
        dialog.dispatchEvent(new CustomEvent('ns:dialog-open'));
      }
      return;
    }
    const closer = event.target.closest?.('[data-ns-dialog-close]');
    if (closer) closer.closest('dialog')?.close();
  });
}
